import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { communityAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import {
  ArrowLeft, Trophy, Users, Crown, Swords, LayoutGrid,
  Clock, Target, CheckCircle2, Lock, Link2
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function CommunityBoard() {
  const { boardId } = useParams();
  useSEO('Community Challenge | JepPrep', 'Take on a community challenge board. One attempt to set your high score on the leaderboard.');
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBoard();
  }, [boardId]);

  const loadBoard = async () => {
    try {
      const response = await communityAPI.getBoard(boardId);
      setBoard(response.data);
    } catch (error) {
      toast.error('Board not found');
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const startChallenge = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to play');
      navigate('/login');
      return;
    }
    try {
      const response = await communityAPI.startChallenge(boardId);
      navigate(`/community/${boardId}/play`, { state: { communityChallenge: true, board: response.data } });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start challenge';
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!board) return null;

  const isChallenge = board.community_challenge;
  const categories = board.categories || [];
  const leaderboard = board.leaderboard || [];
  const hasPlayed = board.user_has_played;
  const userScore = board.user_score;
  const isCreator = user && board.created_by === user.id;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/community">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Community Boards
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-[hsl(var(--brand-gold-500))]/40 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))]/10"
              data-testid="share-board-link-btn"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href).then(() => {
                  toast.success('Challenge link copied! Share it with friends.');
                }).catch(() => {
                  toast.error('Failed to copy link');
                });
              }}
            >
              <Link2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Board Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Meta */}
            <div>
              <div className="flex items-start gap-3 mb-3">
                <h1 className="font-display font-bold text-xl sm:text-3xl text-white break-words" data-testid="board-detail-title">{board.title}</h1>
                {isChallenge && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1">
                    <Swords className="w-3 h-3 mr-1" />
                    Challenge
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span className="capitalize">{board.difficulty || 'Medium'}</span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {board.community_stats?.times_played || 0} plays
                </span>
                <span>by {board.created_by_name || 'Anonymous'}</span>
              </div>
            </div>

            {/* Current Leader */}
            {isChallenge && (
              <Card className="bg-gradient-to-r from-[hsl(var(--brand-blue-800))] to-[hsl(var(--secondary))] border-[hsl(var(--brand-gold-500))]/30">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center shrink-0">
                        <Crown className="w-6 h-6 sm:w-7 sm:h-7 text-[hsl(var(--brand-gold-500))]" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs sm:text-sm">Current Leader</p>
                        <p className="text-white font-bold text-base sm:text-lg truncate max-w-[160px] sm:max-w-none" data-testid="current-leader-name">{board.challenge_leader_name || 'No one yet'}</p>
                      </div>
                    </div>
                    <div className="text-center sm:text-right">
                      <p className="text-gray-400 text-xs sm:text-sm">Score to Beat</p>
                      <p className="text-[hsl(var(--brand-gold-500))] font-bold text-xl sm:text-3xl whitespace-nowrap" data-testid="current-leader-score">
                        ${(board.challenge_score || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Round 1 Categories Preview */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Round 1 Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                {categories.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {categories.map((cat, idx) => (
                      <div key={cat.name || `cat-${idx}`} className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                        <p className="text-[hsl(var(--brand-gold-500))] font-semibold text-sm uppercase truncate">{cat.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{cat.clues?.length || 5} clues</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">No categories available</p>
                )}
              </CardContent>
            </Card>

            {/* Game Info */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-400" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center shrink-0">
                      <span className="text-[hsl(var(--brand-gold-500))] font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">3 Rounds</p>
                      <p className="text-gray-500 text-xs">Round 1, Round 2, and a Final Round wager</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center shrink-0">
                      <span className="text-[hsl(var(--brand-gold-500))] font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Same Clues</p>
                      <p className="text-gray-500 text-xs">Everyone gets the exact same questions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center shrink-0">
                      <span className="text-[hsl(var(--brand-gold-500))] font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">One Attempt</p>
                      <p className="text-gray-500 text-xs">You only get one chance to beat the score</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Play / Status Card */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-6">
                {hasPlayed ? (
                  <div className="text-center">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
                    <p className="text-white font-bold text-lg mb-1">Already Played</p>
                    <p className="text-gray-400 text-sm mb-3">Your score: <span className="text-[hsl(var(--brand-gold-500))] font-bold">${(userScore || 0).toLocaleString()}</span></p>
                    <Button
                      variant="outline"
                      className="w-full border-[hsl(var(--brand-gold-500))]/40 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))]/10 mb-3"
                      data-testid="share-challenge-played-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href).then(() => {
                          toast.success('Challenge link copied! Share it with friends.');
                        }).catch(() => toast.error('Failed to copy link'));
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Share Challenge
                    </Button>
                    <Link to="/community">
                      <Button variant="outline" className="w-full border-[hsl(var(--border))] text-gray-300">
                        Browse More Boards
                      </Button>
                    </Link>
                  </div>
                ) : isCreator ? (
                  <div className="text-center">
                    <Crown className="w-12 h-12 mx-auto text-[hsl(var(--brand-gold-500))] mb-3" />
                    <p className="text-white font-bold text-lg mb-1">Your Board</p>
                    <p className="text-gray-400 text-sm mb-3">You created this challenge. Share it with friends to see if they can beat your score!</p>
                    <Button
                      variant="outline"
                      className="w-full border-[hsl(var(--brand-gold-500))]/40 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))]/10"
                      data-testid="share-challenge-creator-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href).then(() => {
                          toast.success('Challenge link copied! Share it with friends.');
                        }).catch(() => toast.error('Failed to copy link'));
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Share Challenge
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Swords className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
                    <p className="text-white font-bold text-lg mb-2">Ready to Compete?</p>
                    <p className="text-gray-400 text-sm mb-4">
                      Play through 3 rounds with the same clues. Beat ${(board.challenge_score || 0).toLocaleString()} to become the new leader!
                    </p>
                    <Button
                      onClick={startChallenge}
                      className="w-full bg-emerald-600 text-white hover:bg-emerald-700 py-5 text-lg font-semibold"
                      data-testid="beat-the-score-btn"
                    >
                      <Swords className="w-5 h-5 mr-2" />
                      Beat the Score
                    </Button>
                    <div className="flex items-center justify-center gap-1 mt-3 text-gray-500 text-xs">
                      <Lock className="w-3 h-3" />
                      <span>One attempt only</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="leaderboard">
                    {leaderboard.map((entry, idx) => {
                      const isTop = idx === 0;
                      const rankColors = ['text-[hsl(var(--brand-gold-500))]', 'text-gray-300', 'text-orange-400'];
                      return (
                        <div
                          key={entry.display_name || `lb-${idx}`}
                          className={`flex items-center gap-3 p-3 rounded-lg ${isTop ? 'bg-[hsl(var(--brand-gold-500))]/10 border border-[hsl(var(--brand-gold-500))]/20' : 'bg-[hsl(var(--brand-blue-800))]'}`}
                          data-testid={`leaderboard-entry-${idx}`}
                        >
                          <span className={`font-bold text-lg w-6 text-center ${rankColors[idx] || 'text-gray-500'}`}>
                            {entry.rank}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                              {entry.user_name}
                              {entry.is_creator && (
                                <span className="text-gray-500 text-xs ml-1">(creator)</span>
                              )}
                            </p>
                          </div>
                          <span className={`font-bold text-sm ${isTop ? 'text-[hsl(var(--brand-gold-500))]' : 'text-white'}`}>
                            ${(entry.score || 0).toLocaleString()}
                          </span>
                          {isTop && <Crown className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
